import { useEffect, useId, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, Menu, X, Phone } from "lucide-react";
import { WhatsAppIcon } from "./ui-bits";
import { Logo } from "./Logo";
import { useLang, CHROME, PAGES } from "../i18n/LangContext";

function PTFlag({ className }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  return (
    <svg viewBox="0 0 60 60" className={className} aria-hidden="true">
      <defs>
        <clipPath id={`pc${uid}`}><circle cx="30" cy="30" r="30" /></clipPath>
      </defs>
      <g clipPath={`url(#pc${uid})`}>
        <rect width="24" height="60" fill="#046A38" />
        <rect x="24" width="36" height="60" fill="#DA291C" />
        <circle cx="24" cy="30" r="9" fill="#FFE900" stroke="#B8860B" strokeWidth="1.5" />
      </g>
    </svg>
  );
}

function UKFlag({ className }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  return (
    <svg viewBox="0 0 60 60" className={className} aria-hidden="true">
      <defs>
        <clipPath id={`fc${uid}`}><circle cx="30" cy="30" r="30" /></clipPath>
        <clipPath id={`ft${uid}`}><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
      </defs>
      <g clipPath={`url(#fc${uid})`}>
        <g transform="translate(-30,0) scale(2)">
          <rect width="60" height="30" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="3" />
          <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#ft${uid})`} stroke="#C8102E" strokeWidth="2" />
          <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="5" />
          <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="3" />
        </g>
      </g>
    </svg>
  );
}

export const PHONE_DISPLAY = "+351 911 705 084";
export const PHONE_TEL = "tel:+351911705084";
export const WHATSAPP_URL = "https://wa.me/message/HNYXZB46NG3VP1";

function NLFlag({ className }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  return (
    <svg viewBox="0 0 60 60" className={className} aria-hidden="true">
      <defs>
        <clipPath id={`fnl${uid}`}><circle cx="30" cy="30" r="30" /></clipPath>
      </defs>
      <g clipPath={`url(#fnl${uid})`}>
        <rect width="60" height="20" fill="#AE1C28" />
        <rect y="20" width="60" height="20" fill="#F8F6F1" />
        <rect y="40" width="60" height="20" fill="#21468B" />
      </g>
    </svg>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const onInvestors = location.pathname.startsWith("/investors") || location.pathname.startsWith("/pt/investors");
  const onInvestorInfo = location.pathname.startsWith("/investor-info") || location.pathname.startsWith("/pt/investor-info");
  const { lang, lp, setLang } = useLang();
  const c = CHROME[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!langOpen) return;
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-lang-root]")) setLangOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [langOpen]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 bg-cream/85 backdrop-blur-xl border-b border-sand/70">
        <div className="container-x flex items-center justify-between py-3">
          <div className="flex items-center gap-8">
            <Link to={lp("/")} data-testid="nav-logo" aria-label="Verde Capital home">
              <Logo />
            </Link>
            {!onInvestorInfo && (
              <Link
                to={onInvestors ? lp("/") : lp("/investors")}
                data-testid={onInvestors ? "nav-developers-button" : "nav-investors-button"}
                className={`group hidden sm:inline-flex items-center gap-1.5 rounded-full border border-verde-900/10 bg-verde-950/[0.04] px-4 py-2 text-[13px] font-medium text-verde-900/70 transition-all duration-300 hover:border-transparent hover:text-white hover:shadow-lg ${
                  onInvestors
                    ? "hover:bg-gradient-to-r hover:from-[#2563EB] hover:to-[#14B8A6] hover:shadow-[#2563EB]/20"
                    : "hover:bg-gradient-to-r hover:from-verde-800 hover:via-verde-600 hover:to-mint hover:shadow-verde-950/20"
                }`}
              >
                {onInvestors ? c.financing : c.invest}
                <ArrowRight
                  className="h-3.5 w-3.5 opacity-50 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100"
                  aria-hidden="true"
                />
              </Link>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="relative hidden lg:block" data-lang-root>
              <button
                type="button"
                onClick={() => setLangOpen((o) => !o)}
                aria-expanded={langOpen}
                aria-haspopup="true"
                data-testid="lang-switch"
                className="flex items-center gap-2 rounded-full border border-sand py-1.5 pl-2 pr-3 text-xs font-bold text-charcoal transition-colors duration-200 hover:border-verde-800/40"
              >
                <UKFlag className={`h-6 w-6 rounded-full ${lang === "en" ? "" : "hidden"}`} />
                <PTFlag className={`h-6 w-6 rounded-full ${lang === "pt" ? "" : "hidden"}`} />
                <NLFlag className={`h-6 w-6 rounded-full ${lang === "nl" ? "" : "hidden"}`} />
                {lang === "pt" ? "PT" : lang === "nl" ? "NL" : "EN"}
                <ChevronDown className={`h-3 w-3 opacity-60 transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              <div
                className={`absolute right-0 top-full pt-2 transition-all duration-200 ${langOpen ? "visible opacity-100 translate-y-0" : "invisible opacity-0 -translate-y-1"}`}
                data-testid="lang-menu"
              >
                <div className="w-44 rounded-xl border border-sand bg-white p-1.5 shadow-[0_24px_50px_-20px_rgba(7,39,29,0.25)]">
                  <p className="px-3 pb-1.5 pt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-fog">Language</p>
                  <button
                    type="button"
                    onClick={() => { setLang("en"); setLangOpen(false); }}
                    data-testid="lang-option-english"
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${lang === "en" ? "bg-cream/70 text-charcoal" : "text-charcoal/70 hover:bg-cream/50"}`}
                  >
                    <span className="flex items-center gap-2.5"><UKFlag className="h-5 w-5 rounded-full" /> English</span>
                    {lang === "en" && <Check className="h-3.5 w-3.5 text-verde-700" aria-hidden="true" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLang("nl"); setLangOpen(false); }}
                    data-testid="lang-option-dutch"
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${lang === "nl" ? "bg-cream/70 text-charcoal" : "text-charcoal/70 hover:bg-cream/50"}`}
                  >
                    <span className="flex items-center gap-2.5"><NLFlag className="h-5 w-5 rounded-full" /> Nederlands</span>
                    {lang === "nl" && <Check className="h-3.5 w-3.5 text-verde-700" aria-hidden="true" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLang("pt"); setLangOpen(false); }}
                    data-testid="lang-option-portuguese"
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${lang === "pt" ? "bg-cream/70 text-charcoal" : "text-charcoal/70 hover:bg-cream/50"}`}
                  >
                    <span className="flex items-center gap-2.5"><PTFlag className="h-5 w-5 rounded-full" /> Português</span>
                    {lang === "pt" && <Check className="h-3.5 w-3.5 text-verde-700" aria-hidden="true" />}
                  </button>
                </div>
              </div>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp"
              aria-label="Chat with Verde Capital on WhatsApp"
              className="hidden xl:inline-flex h-9 w-9 items-center justify-center rounded-full border border-sand text-verde-800 transition-colors duration-200 hover:bg-sand/70"
            >
              <WhatsAppIcon className="h-4 w-4" />
            </a>
            <a
              href={PHONE_TEL}
              data-testid="nav-phone"
              className="hidden lg:inline-flex items-center gap-2 rounded-full border border-verde-800/25 px-4 py-2 text-sm font-semibold text-verde-800 transition-colors duration-200 hover:border-verde-800 hover:bg-verde-800 hover:text-white"
            >
              <Phone className="h-4 w-4" />
              {PHONE_DISPLAY}
            </a>
            <Link
              to={lp("/book")}
              data-testid="nav-cta"
              className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-verde-800 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-verde-700"
            >
              {c.book}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
            <button
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full text-verde-900"
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] bg-verde-950 flex flex-col" data-testid="mobile-menu">
          <div className="container-x flex items-center justify-between py-4">
            <img loading="lazy" decoding="async" src="/logo-white.png" alt="Verde Capital" className="h-[22px] sm:h-7 w-auto" />
            <button
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-white"
              onClick={() => setOpen(false)}
              data-testid="nav-menu-close"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="container-x mt-6 flex flex-1 flex-col overflow-y-auto pb-8">
            <Link
              to={lp("/book")}
              className="mt-4 inline-flex w-fit items-center gap-2.5 rounded-full bg-mint px-6 py-3.5 text-sm font-semibold text-verde-950"
              data-testid="mobile-cta"
            >
              {c.book} <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-7 font-mono text-[11px] uppercase tracking-[0.22em] text-mint/70">{c.explore}</p>
            <div className="mt-2 grid grid-cols-2 gap-x-6">
              {["/products/pre-construction-finance", "/book", "/brokers", "/brokers/submit", "/expert-network", "/about", "/contact", "/faq"].map((to) => (
                <Link
                  key={to}
                  to={lp(to)}
                  className="flex items-center border-b border-cream/10 py-3 text-[15px] font-medium text-cream/90 transition-colors hover:text-white"
                  data-testid={`mobile-explore-${PAGES[to].en.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {PAGES[to][lang]}
                </Link>
              ))}
            </div>
            <div className="mt-6 border-t border-cream/10 pt-5" data-testid="mobile-lang">
              <button
                type="button"
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`flex w-full items-center justify-between py-2.5 text-sm font-semibold ${lang === "en" ? "text-white" : "text-cream/60"}`}
              >
                <span className="flex items-center gap-3"><UKFlag className="h-6 w-6 rounded-full" /> EN — English</span>
                {lang === "en" && <Check className="h-4 w-4 text-mint" aria-hidden="true" />}
              </button>
              <button
                type="button"
                onClick={() => setLang("pt")}
                data-testid="mobile-lang-pt"
                className={`flex w-full items-center justify-between py-2.5 text-sm font-semibold ${lang === "pt" ? "text-white" : "text-cream/60"}`}
              >
                <span className="flex items-center gap-3"><PTFlag className="h-6 w-6 rounded-full" /> PT — Português</span>
                {lang === "pt" && <Check className="h-4 w-4 text-mint" aria-hidden="true" />}
              </button>
              <button
                type="button"
                onClick={() => setLang("nl")}
                data-testid="mobile-lang-nl"
                className={`flex w-full items-center justify-between py-2.5 text-sm font-semibold ${lang === "nl" ? "text-white" : "text-cream/60"}`}
              >
                <span className="flex items-center gap-3"><NLFlag className="h-6 w-6 rounded-full" /> NL — Nederlands</span>
                {lang === "nl" && <Check className="h-4 w-4 text-mint" aria-hidden="true" />}
              </button>
            </div>
            <div className="mt-5 flex items-center gap-6 border-t border-cream/10 pt-6">
              <a
                href={PHONE_TEL}
                data-testid="mobile-phone"
                className="flex items-center gap-2 py-2 text-sm font-semibold text-mint"
              >
                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp"
                className="flex items-center gap-2 py-2 text-sm font-medium text-cream/80"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
